/**
 * 三线 DTO → `WorkItem` 的**唯一投影**(F1-b 从 `pages/works/model/workLines.ts` 提上来)。
 *
 * ★为什么提到 entities:`/works`(pages 层)与三个 hub 页的「最近 6 条」(features 层)都要这份投影,
 * 而 FSD 单向核心禁 features import pages。放这里两边都能用,投影口径只有一份。
 *
 * ★入参是**结构化最小类型**不是 feature 的 DTO 类型:entities 不 import features
 * (同 `entities/advisor-chat/api/creationsApi` 的既有做法)。三个 feature 的 DTO 结构上满足这些
 * 接口,直接传进来即可;真正的字段契约由各 feature 的 service 类型保证,这里只声明**用到的那几个**。
 */
import { estSecondsFromChars } from "@/shared/lib/dhSpeech";
import { videoProviderLabel } from "@/shared/lib/video/labels";
import { sceneLabelOf } from "@/entities/scene-card/catalog";
import { videoSceneKeyFor } from "@/entities/scene-card/model/briefPurpose";
import { LONG_IMAGE_USAGES, REPORTER_GENRE_SCENE_KEY } from "@/entities/scene-card/model/presets";
import { DEPT_TITLE } from "./deptTitles";
import { longImageDisplayName, longImageSceneKeyOfName, parseLongImageName } from "./longImageName";
import type { MediaQueueInfo } from "@/shared/lib/queueInfo";
import type { WorkItem } from "./types";

export interface RawPosterTask {
  id: number;
  name?: string | null;
  status: string;
  sceneKey?: string | null;
  requestedCount?: number;
  /** P1-4:上传素材任务 provider="UPLOAD"(backend PosterImportService 契约),卡面要与真出图区分。 */
  provider?: string | null;
  resolutionTier?: string | null;
  /**
   * 实交档(1006 演示机复测:2K 单实际按 1K 交付,卡上仍标「2K」)。api 列表给了就以它为准
   * (规则同 features/poster/model/deliveredTier:按实交像素判档);没给 = 只知道请求档。
   */
  deliveredTier?: string | null;
  createdAt: string;
  completedAt?: string | null;
  coverThumbUrl?: string | null;
  creatorUserName?: string | null;
  isFavorite?: boolean;
  errorMessage?: string | null;
  /** 1104 排队信息(仅 PENDING 非 null;老后端无此键)。 */
  queueInfo?: MediaQueueInfo | null;
}

export interface RawVideoTask {
  id: number;
  name?: string | null;
  status: string;
  sceneKey?: string | null;
  /** 目的档(8 值 label);0813 后端列表出参补的,历史任务无。 */
  purpose?: string | null;
  provider?: string | null;
  /** 内部 model_code(不对客展示)。 */
  model?: string | null;
  /** 对客模型档位名(OEM H2 后端直出);老后端无此键。 */
  modelLabel?: string | null;
  resolution?: string | null;
  durationSec?: number | null;
  createdAt: string;
  finishedAt?: string | null;
  coverThumbUrl?: string | null;
  creatorUserName?: string | null;
  isFavorite?: boolean;
  errorMessage?: string | null;
  /** 1104 排队信息(仅 PENDING 非 null;老后端无此键)。 */
  queueInfo?: MediaQueueInfo | null;
}

export interface RawDhTask {
  id: number;
  name?: string | null;
  status: string;
  mode?: string | null;
  sceneKey?: string | null;
  createdAt?: string | null;
  completedAt?: string | null;
  coverThumbUrl?: string | null;
  creatorUserName?: string | null;
  isFavorite?: boolean;
  errorMessage?: string | null;
  /** 1104 排队信息(仅 PENDING 非 null;老后端无此键)。 */
  queueInfo?: MediaQueueInfo | null;
  formulaName?: string | null;
  /** 设置回显(api 列表同样下发,DigitalHumanPayloadMapper.toTaskDto);老任务 null。只用到这两个字段。 */
  settings?: { script?: string | null; resolution?: string | null } | null;
}

/** 分身片型 → 作品卡档位短名(与创建页 DH_MODE_OPTIONS 的 label 同义,去掉「(推荐)」等修饰)。 */
const DH_MODE_CARD_LABEL: Record<string, string> = {
  flagship: "照片生动版",
  longform: "照片省流版",
  video: "真人换口播",
  tts: "文生语音",
};

/**
 * 分身作品卡 meta(1006 演示机最终回归:照片数字人卡没有档位/时长,视频卡有「标准·图生视频 · 720p · 5s」)。
 * = 档位 · 分辨率 · 约 N 秒(按文案字数估,与详情页「换音色」报价同一个 estSecondsFromChars 口径 ——
 * DTO 不回实际时长,故标「约」);缺哪项不显哪项;套用蓝图的仍追加「套用:X」。
 */
export function dhCardMeta(t: RawDhTask): string | null {
  const chars = t.settings?.script?.trim().length ?? 0;
  const parts = [
    t.mode ? DH_MODE_CARD_LABEL[t.mode] : null,
    t.mode === "tts" ? null : t.settings?.resolution?.toLowerCase() || null,
    chars > 0 ? `约 ${estSecondsFromChars(chars)}s` : null,
    t.formulaName ? `套用:${t.formulaName}` : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : null;
}

export interface RawReporterDraft {
  id: number;
  title?: string | null;
  genre: string;
  subType?: string | null;
  status: string;
  targetWords?: number | null;
  // N-11(0811 反馈):后端 DraftSummaryDto 补的创建人姓名与封面缩略图(旧后端无此字段 = undefined,照旧兜底)
  creatorUserName?: string | null;
  coverThumbUrl?: string | null;
  updatedAt: string;
}

/**
 * 官媒舱草稿状态机(DRAFT→GENERATING→GENERATED→EDITED / FAILED)→ media_task 七态词汇。
 * works 的徽标/筛选 chips/在途判定都只认后者;EDITED 是「成功后又改过」,对列表就是 SUCCESS。
 */
const REPORTER_TO_TASK_STATUS: Record<string, string> = {
  DRAFT: "PENDING",
  GENERATING: "RUNNING",
  GENERATED: "SUCCESS",
  EDITED: "SUCCESS",
  FAILED: "FAILED",
};

/** 体裁 → 场景卡 key:单源在 scene-card presets(卡 key / 打标 / 场景筛三处同一张表)。 */
const REPORTER_GENRE_SCENE: Record<string, string> = REPORTER_GENRE_SCENE_KEY;

/** 官媒舱:产物是文章(无封面);卡面场景名从体裁推,时间只有 updatedAt 一个口径。 */
export function mapReporter(d: RawReporterDraft): WorkItem {
  return {
    uid: `jizhe-${d.id}`,
    line: "jizhe",
    id: d.id,
    lineLabel: DEPT_TITLE.jizhe,
    title: d.title || `文章 #${d.id}`,
    // N-11:两列此前写死 null 是因为 DTO 没带 —— 后端补上了,这里如实透出(缺失仍 null)
    coverUrl: d.coverThumbUrl ?? null,
    status: REPORTER_TO_TASK_STATUS[d.status] ?? d.status,
    sceneKey: REPORTER_GENRE_SCENE[d.genre] ?? null,
    sortAt: d.updatedAt,
    ownerName: d.creatorUserName ?? null,
    meta: d.targetWords ? `目标 ${d.targetWords} 字` : null,
    isFavorite: false,
    errorMessage: null,
    detailRoute: `/ai-jizhe/workbench/${d.id}`,
  };
}

const TIER_RANK: Record<string, number> = { "1K": 0, "2K": 1, "4K": 2 };

/** 卡面档位:实交低于所选 → 标实交档并注明所选(「 · 1K(所选 2K)」);否则标所选档。 */
export function posterTierText(
  requested: string | null | undefined,
  delivered: string | null | undefined
): string {
  const req = (requested ?? "").toUpperCase();
  const got = (delivered ?? "").toUpperCase();
  if (got in TIER_RANK && req in TIER_RANK && TIER_RANK[got] < TIER_RANK[req]) {
    return ` · ${got}(所选 ${req})`;
  }
  return requested ? ` · ${requested}` : "";
}

export function mapPoster(t: RawPosterTask): WorkItem {
  const count = t.requestedCount ?? 0;
  // P1-4(0829 雷雷):上传素材(provider="UPLOAD",不出图不扣费)的 meta 不摆「N 张 · 档位」
  // 那套出图口径 —— 顶着出图元信息和真出图任务混列,用户会以为 AI 出过一张图。
  // 字面量对比不引 features(FSD:entities 不 import features);值与 backend 写入同一契约。
  const isUpload = t.provider === "UPLOAD";
  // 1006:一键长图由 api 建单不带 sceneKey,用途只在名字的用途段里(见 longImageName 头注)——
  //   有用途段 → 标签按用途卡(「企业介绍长图」),名字去掉「一键长图·企业介绍长图·」只留主题·时间。
  return {
    uid: `poster-${t.id}`,
    line: "poster",
    id: t.id,
    lineLabel: DEPT_TITLE.poster,
    title: longImageDisplayName(t.name) || `海报任务 #${t.id}`,
    coverUrl: t.coverThumbUrl ?? null,
    status: t.status,
    sceneKey: t.sceneKey ?? longImageSceneKeyOfName(t.name),
    // 完成时间优先:列表按「什么时候做完的」排,才与用户记忆一致(提交顺序 ≠ 完成顺序)。
    sortAt: t.completedAt ?? t.createdAt,
    ownerName: t.creatorUserName ?? null,
    meta: isUpload
      ? "上传素材 · 非 AI 出图"
      : count > 0
        ? `${count} 张${posterTierText(t.resolutionTier, t.deliveredTier)}`
        : null,
    isFavorite: !!t.isFavorite,
    errorMessage: t.errorMessage ?? null,
    ...(t.queueInfo ? { queueInfo: t.queueInfo } : {}),
    detailRoute: `/ai-haibao/tasks/${t.id}`,
  };
}

/** 一键长图仍在跑的作业(读懂+排区块+派发那一段,还没有 xb_media_task 行)。 */
export interface RawPosterOneClickJob {
  jobId: number;
  draftId?: number | null;
  /**
   * QUEUED/RUNNING = 在途卡;FAILED(屏卡 P1,0910)= 近期失败卡,带 reason ——
   * 此前 FAILED 被整个滤掉,用户关掉搭建器页后一键失败在作品库彻底不可见
   * (0910 真实用户 9 连杀全程无感的三个盲区之一)。DONE 仍不进来(真任务卡覆盖)。
   */
  status: "QUEUED" | "RUNNING" | "FAILED";
  /** FAILED 时的人话原因(质检闸/塌缩护栏产的「这单没出图、费用不扣。问题:…」)。 */
  reason?: string | null;
  createdAt?: string | null;
  /**
   * 这单的长图用途(goods/corp/…;Z9d 0926)—— 与创建时**同一来源**:一键提交前 ensureDraft 把
   * `presetUsage` 写进作业锚壳 formJson 的 `useCase`,作品页从草稿列表按 draftId 读回来
   * (`draftShellUseCase`)。作业表自身没有这一列(api MediaDraftJobDto 无 usage)。
   * 缺省/null = 读不到(壳已清 / 直进无用途):路由不带 usage,搭建器按直进渲染。
   */
  usage?: string | null;
  /**
   * 作业锚壳草稿的名字(1005)——一键提交前 ensureDraft 已按 `longImageDraftName` 起好
   * 「详情长图·{产品名|正文首行}·MM-DD HH:mm」,与智能编排草稿卡同一套。作品页从草稿列表按 draftId 读回。
   * 缺省 = 读不到(壳已清 / 网络),退回「一键长图 #jobId」。
   */
  draftName?: string | null;
}

/**
 * 一键在途卡标题(1005 生产验收:此前恒为「一键长图 #6286998503424」纯编号,几单分不清)。
 * 取锚壳草稿名(与智能编排草稿卡同一套命名);它的「详情长图·」前缀换成「一键长图·」——
 * 一键是另一条路,且企业介绍等用途并不是「详情」长图。读不到名字退回编号。
 */
export function oneClickJobTitle(jobId: number, draftName?: string | null): string {
  const n = (draftName ?? "").trim();
  if (!n || /^草稿 #/.test(n)) return `一键长图 #${jobId}`;
  // 1006:名字带用途段(「详情长图·企业介绍长图·主题·时间」)→ 只留主题·时间,用途走卡片标签(sceneKey)
  const parsed = parseLongImageName(n);
  if (parsed?.usage) return parsed.rest;
  return n.startsWith("详情长图·") ? `一键长图·${n.slice("详情长图·".length)}` : n;
}

/**
 * 一键长图「点完即走」在途卡(2026-09-04)——`mapPoster` 覆盖不到这段窗口:
 * 一键出图提交后先跑「读懂 + 排区块 + 派发」,这一段完全没有 `xb_media_task` 行
 * (AFTER_COMMIT 那一步才建),不补这一份,用户点完一键切到作品页,这段时间那单
 * 在「进行中」节里彻底不存在 —— 正是本仓要修的「点完即走」的地基。
 *
 * ★`detailRoute` 指回搭建器(带 draftId)而不是任务详情:此刻还没有任务可看,
 *   搭建器自己会据 `useCanvasBuilder` 的恢复扫描认出这单、续接轮询(同一张作业表,单一真相源)。
 * ★status 词汇跟 `media_task` 七态对齐(QUEUED→PENDING,RUNNING→RUNNING)——
 *   `taskStatusLabel`/`isTaskActive` 才认得,作品页的「进行中」5s 轮询才会继续转。
 * ★`detailRoute` 带 `&usage=`(Z9d 0926):只带 draftId 回去,搭建器 presetUsage 为空 →
 *   默认落「智能编排」路卡(失败块藏在一键路卡后)、备料按 default 桶读(goods 桶的料不见,
 *   「再试一次」置灰要重打料)、价签变成并集宽区间。usage 与 `?usage=` 读侧同一份白名单
 *   (LONG_IMAGE_USAGES),验不过不带 —— 一个坏值不该让搭建器按错用途渲染。
 */
export function mapPosterOneClickJob(j: RawPosterOneClickJob): WorkItem {
  const usage =
    j.usage && (LONG_IMAGE_USAGES as readonly string[]).includes(j.usage) ? j.usage : null;
  const usageQuery = usage ? `&usage=${usage}` : "";
  return {
    uid: `poster-oneclick-${j.jobId}`,
    line: "poster",
    id: j.jobId,
    lineLabel: DEPT_TITLE.poster,
    title:
      j.status === "FAILED"
        ? `${oneClickJobTitle(j.jobId, j.draftName)} · 没排成`
        : oneClickJobTitle(j.jobId, j.draftName),
    coverUrl: null,
    status: j.status === "QUEUED" ? "PENDING" : j.status === "FAILED" ? "FAILED" : "RUNNING",
    // 1006:与完成后的作品卡同一标签口径 —— 非电商详情用途显示用途卡名(「企业介绍长图」)
    sceneKey:
      usage && usage !== "goods" ? `longimage_${usage}` : longImageSceneKeyOfName(j.draftName),
    sortAt: j.createdAt ?? "",
    // 作业 DTO 没有创建人;接口按 owner 过滤 → 一定是当前登录人的(卡面据此显示登录人名)
    ownerName: null,
    selfOwned: true,
    meta: null,
    isFavorite: false,
    errorMessage: j.status === "FAILED" ? (j.reason ?? "排版没排成,费用不扣") : null,
    detailRoute:
      j.draftId != null
        ? `/ai-haibao/detail-long?draftId=${j.draftId}${usageQuery}`
        : "/ai-haibao/detail-long",
  };
}

export function mapVideo(t: RawVideoTask): WorkItem {
  const parts = [
    videoProviderLabel(t.provider, t.model, t.modelLabel),
    t.resolution,
    t.durationSec && `${t.durationSec}s`,
  ];
  return {
    uid: `video-${t.id}`,
    line: "video",
    id: t.id,
    lineLabel: DEPT_TITLE.video,
    title: t.name || `视频任务 #${t.id}`,
    coverUrl: t.coverThumbUrl ?? null,
    status: t.status,
    sceneKey: t.sceneKey ?? null,
    // 0813:品类回落 —— sceneKey 只有「从场景卡进来」的片才有,而目的每条片都有。
    // 走 sceneLabelOf(videoSceneKeyFor(...)) 而不是直接用目的字面量:卡面上的名字要和
    // 场景卡是同一套(下线目的 → videoSceneKeyFor 返 null → 这里也 null,不打假标)。
    categoryLabel: sceneLabelOf(videoSceneKeyFor(t.purpose)),
    sortAt: t.finishedAt ?? t.createdAt,
    ownerName: t.creatorUserName ?? null,
    meta: parts.filter(Boolean).join(" · "),
    isFavorite: !!t.isFavorite,
    errorMessage: t.errorMessage ?? null,
    ...(t.queueInfo ? { queueInfo: t.queueInfo } : {}),
    detailRoute: `/ai-video/tasks/${t.id}`,
  };
}

/** 分身:mode=tts 是纯音频档(无画面),标题兜底要说清是语音还是口播,别一律叫「口播视频」。 */
export function mapDh(t: RawDhTask): WorkItem {
  const isAudio = t.mode === "tts";
  return {
    uid: `dh-${t.id}`,
    line: "dh",
    id: t.id,
    lineLabel: DEPT_TITLE.dh,
    title: t.name || `${isAudio ? "文生语音" : "口播视频"} #${t.id}`,
    coverUrl: t.coverThumbUrl ?? null,
    status: t.status,
    sceneKey: t.sceneKey ?? null,
    sortAt: t.completedAt ?? t.createdAt ?? "",
    ownerName: t.creatorUserName ?? null,
    meta: dhCardMeta(t),
    audio: isAudio,
    variant: t.mode ?? null,
    isFavorite: !!t.isFavorite,
    errorMessage: t.errorMessage ?? null,
    ...(t.queueInfo ? { queueInfo: t.queueInfo } : {}),
    detailRoute: `/ai-digital-human/tasks/${t.id}`,
  };
}

/**
 * 跨线合并 —— **k 路归并**,不是「各取几条拼一拼」。
 *
 * 每条线各自按时间倒序返回前 K 条,取三条线的前 K 条合并后再截前 K,得到的就是**真正的全局前 K**
 * (被截掉的那些,其所在线内已经有 K 条比它新,不可能进全局前 K)。所以「加载更多」是把 K 加大
 * 重取,而不是各线翻页拼接 —— 后者在三线速度不均时会漏条(某线第 2 页的第 1 条可能比另一线
 * 第 1 页的末条更新)。
 *
 * 空 `sortAt` 的条目丢弃:它排不进任何位置,留着只会在列表里随机浮动(分身历史任务的
 * completedAt/createdAt 都可能为 null)。
 */
export function mergeWorks(lists: WorkItem[][], limit: number): WorkItem[] {
  return lists
    .flat()
    .filter((w) => w.sortAt)
    .sort((a, b) => (a.sortAt < b.sortAt ? 1 : a.sortAt > b.sortAt ? -1 : 0))
    .slice(0, limit);
}
